import React from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import {
  Box,
  Typography,
  Button,
  FormikForm,
  FormikFormContainer,
  TextInputForm,
  PhoneNumberInput,
  Checkbox,
  Avatar,
  Sidepanel,
} from "@ridecell/web-components-material"
import * as Yup from "yup"
import { makeStyles } from "@material-ui/core/styles"
import { Field, FieldArray } from "formik"
import { Ridecell } from "@ridecell/web-sdk"
import Snackbar from "@material-ui/core/Snackbar"
import MuiAlert from "@material-ui/lab/Alert"

import VpnKeyIcon from "@material-ui/icons/VpnKey"
import CloseIcon from "@material-ui/icons/Close"
import ExitToAppIcon from "@material-ui/icons/ExitToApp"
import RefreshIcon from "@material-ui/icons/Refresh"
import useGlobalContext from "../../utils/customHooks/useGlobalContext"
import LoadingButton from "../LoadingButton"
import { getErrorMessage } from "../../utils/common"
import messages from "./messages"
import PaymentMethods from "./PaymentMethods"
import useCustomer from "../../utils/customHooks/useCustomer"

const alphaNumeric = /^[a-zA-Z0-9]+$/

const validationSchema = ({
  tenantSettings,
  formatMessage,
  isPrivateProfile,
}) =>
  Yup.object().shape({
    phone_number: tenantSettings.phoneNumberEditingEnabled
      ? Yup.string().required(formatMessage(messages.phoneNumberIsRequired))
      : Yup.string().nullable(),
    external_driver_id:
      tenantSettings.enableExternalDriverId &&
      isPrivateProfile &&
      Yup.string()
        .required(formatMessage(messages.externalDriverIdRequired))
        .matches(
          alphaNumeric,
          formatMessage(messages.externalDriverIdShouldBeAlphaNumeric)
        )
        .nullable(),
    external_membership_number:
      tenantSettings.collectExternalMembershipNumber &&
      tenantSettings.requireExternalMembershipNumber
        ? Yup.string().required(
            formatMessage(messages.externalMembershipNumberIsRequired)
          )
        : Yup.string().nullable(),
    mailing_address_display:
      tenantSettings.enableExternalDriverId &&
      Yup.string().required(formatMessage(messages.required)).nullable(),
  })

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  profileSection: {
    padding: "16px 16px 24px 16px",
    marginLeft: "-16px",
    marginRight: "-16px",
    background: "white",
    borderRadius: "0",
  },
  titleBox: {
    padding: "8px 16px",
  },
  emailText: {
    marginLeft: "16px",
  },
  cancelButton: {
    color: "#F44336",
    fontWeight: 500,
  },
  large: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  cancelButton: {
    color: "red",
    fontWeight: 500,
    marginLeft: "12px",
  },
  signoutIcon: {
    color: "red",
  },
}))

const ProfilePanel = ({
  open,
  closePanel,
  onLogout,
  initials,
  intl: { formatMessage },
  fetchUpdateCustomer,
  getConsentOptions,
  isMobile,
  history,
  togglePaymentModal,
  profileMode,
}) => {
  const classes = useStyles({ isMobile })
  const {
    state: { customer, tenantId, tenantSettings, consentDecisions, loading },
    setLoading,
    toggleBackdrop,
    setCurrentUser,
  } = useGlobalContext()
  const [snackBar, setSnackbar] = React.useState({
    open: false,
    vertical: "bottom",
    horizontal: "left",
    text: "",
    severity: "success",
  })
  const [cards, setCards] = React.useState([])
  const { fetchCustomer } = useCustomer()
  const showB2BinviteField =
    customer.profile_type === "PERSONAL" &&
    tenantSettings.businessToBusinessEnabled
  const enableExternalDriverId = tenantSettings.enableExternalDriverId
  const showAdditionalInfo =
    tenantSettings.collectExternalMembershipNumber ||
    showB2BinviteField ||
    enableExternalDriverId
  const sidePanelAnchor = isMobile ? "bottom" : "right"
  const sidePanelWidth = isMobile ? "mobile" : "small"
  const hideConsentOptions = consentDecisions.every(
    (option) => option.checkbox_default === null
  )
  const showExternalDriverId =
    enableExternalDriverId &&
    ((customer && customer.profile_type === "PERSONAL") ||
      profileMode === "personal")

  const fetchCards = async () => {
    if (tenantSettings.paymentsEnabled) {
      try {
        toggleBackdrop(true)
        const cards = await Ridecell.getCards(customer.id)
        setCards(cards)
      } finally {
        toggleBackdrop(false)
      }
    }
  }

  React.useEffect(() => {
    if (open) {
      ;(async () => {
        await fetchCards()
      })()
    }
  }, [open])

  const resendPassword = async () => {
    toggleBackdrop(true)
    try {
      const email = customer.email
      const phone = customer.phone_number
      tenantSettings?.passwordResetNotificationType === "email"
        ? await Ridecell.resetPassword({ customerContact: email })
        : await Ridecell.resetPassword({ customerContact: phone })
      // TODO: Format the below copy changes
      setSnackbar({
        ...snackBar,
        open: true,
        severity: "success",
        text: formatMessage(messages.successfullySent, {
          communication: tenantSettings?.passwordResetNotificationType
            ? "email"
            : "sms",
        }),
      })
    } catch ({ message: [response] }) {
      setSnackbar({ ...snackBar, open: true, text: response })
    } finally {
      toggleBackdrop(false)
    }
  }

  const panelActions = () => {
    return (
      <Box
        display="flex"
        sx={{ p: "20px" }}
        width="100%"
        justifyContent="center"
      >
        <Button
          color="primary"
          size="large"
          startIcon={<ExitToAppIcon className={classes.signoutIcon} />}
          variant="outlined"
          onClick={onLogout}
        >
          <FormattedMessage {...messages.signOut} />
        </Button>
      </Box>
    )
  }

  const Profile = () => {
    return (
      <Box
        display="flex"
        sx={{ mb: "16px" }}
        flexDirection="column"
        alignItems="center"
        alignContent="center"
        justifyContent="center"
      >
        <Avatar className={classes.large}>{initials()}</Avatar>
        <Typography className={classes.titleBox} variant="h6">
          {customer.full_name}
        </Typography>
        <Box
          display="flex"
          sx={{ mt: "4px", mb: "16px" }}
          flexDirection="column"
        >
          <Box
            display="flex"
            sx={{
              mb: "8px",
              mr: "12px",
              alignSelf: "flex-end",
              flexWrap: "wrap",
            }}
            alignItems="end"
          >
            <Typography variant="body">
              {formatMessage(messages.email)}
            </Typography>
            <Typography className={classes.emailText} variant="subtitle2">
              {customer.email}
            </Typography>
          </Box>
          {customer?.business_email && (
            <Box
              display="flex"
              sx={{ mr: "12px", alignSelf: "flex-end", flexWrap: "wrap" }}
              alignItems="end"
            >
              <Typography variant="body">
                {formatMessage(messages.businessEmail)}
              </Typography>
              <Typography className={classes.emailText} variant="subtitle2">
                {customer.business_email}
              </Typography>
            </Box>
          )}
          {!tenantSettings.customer_editing_phone_number_enabled &&
            customer.phone_number && (
              <Box
                display="flex"
                sx={{
                  mr: "12px",
                  alignSelf: "flex-end",
                  mt: "8px",
                  flexWrap: "wrap",
                }}
                alignItems="end"
              >
                <Typography variant="body">
                  {formatMessage(messages.mobile)}
                </Typography>
                <Typography className={classes.emailText} variant="subtitle2">
                  {customer.phone_number}
                </Typography>
              </Box>
            )}
        </Box>

        <Button
          color="primary"
          size="large"
          fullWidth
          startIcon={<VpnKeyIcon />}
          variant="outlined"
          onClick={resendPassword}
        >
          <FormattedMessage {...messages.resetPassword} />
        </Button>
      </Box>
    )
  }

  const initializeConsentOptions = () => {
    return consentDecisions
      .map((option) => {
        if (option.checkbox_user !== null && option.checkbox_user)
          return option.id
        if (option.checkbox_user === null && option.checkbox_default)
          return option.id
        if (option.checkbox_default === null) return undefined
      })
      .filter((id) => id)
  }

  const shapeInitialValues = () => {
    return {
      phone_number: customer?.phone_number || "",
      mailing_address_display: customer?.mailing_address_display || "",
      business_profile_activation_code:
        customer?.business_profile_activation_code || "",
      consent_options: initializeConsentOptions(),
      external_membership_number: customer?.external_membership_number || "",
      external_driver_id: customer?.external_driver_id || "",
    }
  }

  const hasKeyChanged = (values, key) =>
    ![null, customer[key], ""].includes(values[key]) ||
    (customer[key] !== null && values[key] === "")

  const onSubmit = async (values) => {
    try {
      setLoading(true)
      let newCustomerData = {}
      const isExternalDriverIdChanged =
        enableExternalDriverId && hasKeyChanged(values, "external_driver_id")
      const isMembershipIdChanged =
        tenantSettings.collectExternalMembershipNumber &&
        hasKeyChanged(values, "external_membership_number")
      if (values.phone_number !== customer.phone_number) {
        newCustomerData["phone_number"] = values.phone_number
        const resp = await Ridecell.updateCustomer({
          customerId: customer.id,
          data: newCustomerData,
        })
        setCurrentUser(resp)
        if (tenantSettings.customerVerificationModeString === "sms") {
          history.push({
            pathname: `/signup`,
            state: { defaultSteps: ["Account Verification"] },
          })
        }
      }
      if (
        values.mailing_address_display !== customer.mailing_address_display ||
        isExternalDriverIdChanged
      ) {
        if (values?.mailing_address_display?.trim() === "") {
          await Ridecell.deleteMailingAddress(customer.id)
        } else {
          await Ridecell.saveMailingAddress({
            customerId: customer.id,
            requestType: "patch",
            data: {
              mailing_address_display: values.mailing_address_display,
              locale: "en-US",
              ...(enableExternalDriverId && {
                external_driver_id: values.external_driver_id,
              }),
            },
          })
        }
        await fetchCustomer()
      }
      if (isMembershipIdChanged) {
        newCustomerData["external_membership_number"] =
          values.external_membership_number
      }
      if (Object.keys(newCustomerData).length > 0) {
        const resp = await Ridecell.updateCustomer({
          customerId: customer.id,
          data: newCustomerData,
        })
        setCurrentUser(resp)
      }
      if (
        showB2BinviteField &&
        values.business_profile_activation_code !== ""
      ) {
        const { business_profile_activation_code } = values
        await Ridecell.linkBusinessProfile({
          customerId: customer.id,
          data: {
            business_profile_activation_code,
          },
        })
        await fetchUpdateCustomer()
      }
      if (
        JSON.stringify(values.consent_options) !==
        JSON.stringify(initializeConsentOptions())
      ) {
        const consentData = consentDecisions.map((option) => {
          return {
            consent_option: option.id,
            option_version: option.option_version,
            user_decision: values.consent_options.includes(option.id),
          }
        })
        await Ridecell.createConsent({
          data: consentData,
          queryParams: { user_id: customer.uuid, tenant_id: tenantId },
        })
        await getConsentOptions()
      }
      // TODO: Format the below copy changes
      setSnackbar({
        ...snackBar,
        open: true,
        severity: "success",
        text: formatMessage(messages.updatesSavedSuccessfully),
      })
    } catch (e) {
      setSnackbar({
        ...snackBar,
        open: true,
        severity: "error",
        text: getErrorMessage(e),
      })
    } finally {
      setLoading(false)
    }
  }

  const validateMembershipNumber = (val) => {
    let error
    let regExp = new RegExp("^\\d+$")
    const isValid = regExp.test(val)
    if (val && !isValid) {
      error = formatMessage(messages.membershipNumberShouldBeDigits)
    } else if (val && val.length !== 16) {
      error = formatMessage(messages.membershipNumberMustBeDigits)
    } else if (val && !val.startsWith("620") && !val.startsWith("429")) {
      // AAA Membership numbers need to start with 620/429
      error = formatMessage(messages.membershipNumberShouldStartsWithEither)
    }
    return error
  }

  const onAddCard = () => {
    closePanel()
    togglePaymentModal(true)
  }

  const onDeleteCard = async (id) => {
    try {
      toggleBackdrop(true)
      await Ridecell.deleteCard({
        customerId: customer.id,
        data: { card_id: id },
      })
      await fetchCards()
    } finally {
      toggleBackdrop(false)
    }
  }

  const markAsDefault = async (id) => {
    try {
      toggleBackdrop(true)
      await Ridecell.markCardAsDefault({
        customerId: customer.id,
        data: { card_id: id },
      })
      await fetchCards()
    } finally {
      toggleBackdrop(false)
    }
  }

  return (
    <Sidepanel
      open={open}
      panelActions={panelActions()}
      closeAction={closePanel}
      anchor={sidePanelAnchor}
      width={sidePanelWidth}
      menu={false}
      title={formatMessage(messages.profile)}
    >
      <Box>
        <FormikFormContainer
          initialValues={shapeInitialValues()}
          enableReinitialize={true}
          onSubmit={async (values) => {
            onSubmit(values)
          }}
          validationSchema={validationSchema({
            tenantSettings,
            consentDecisions,
            formatMessage,
            isPrivateProfile: showExternalDriverId,
          })}
        >
          {({ isValid, values, dirty, setFieldValue, resetForm }) => (
            <FormikForm>
              <Box
                className={classes.profileSection}
                flexDirection="row"
                header={false}
              >
                {Profile()}
                {tenantSettings.phoneNumberEditingEnabled && (
                  <Box display={"flex"} sx={{ mb: "16px" }}>
                    <PhoneNumberInput
                      id="phone-input"
                      label={formatMessage(messages.phoneNumber)}
                      name="phone_number"
                      onChange={(phone) => {
                        setFieldValue("phone_number", phone)
                      }}
                      required
                      type="text"
                      value={values.phone_number || ""}
                    />
                  </Box>
                )}
                <Box display={"flex"} sx={{ mb: "16px" }}>
                  <TextInputForm
                    color="secondary"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label={formatMessage(messages.mailingAddress)}
                    name="mailing_address_display"
                    required={enableExternalDriverId}
                    value={values.mailing_address_display}
                  />
                </Box>
                {showExternalDriverId && (
                  <Box display={"flex"} sx={{ mb: "16px" }}>
                    <TextInputForm
                      color="secondary"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        maxLength: 64,
                      }}
                      label={formatMessage(messages.externalDriverId)}
                      required
                      name="external_driver_id"
                      value={values.external_driver_id}
                    />
                  </Box>
                )}
              </Box>
              {showAdditionalInfo && (
                <React.Fragment>
                  <Typography className={classes.titleBox} variant="subtitle2">
                    {formatMessage(messages.additionalInformation)}
                  </Typography>
                  <Box
                    className={classes.profileSection}
                    flexDirection="row"
                    header={false}
                  >
                    {customer.profile_type === "PERSONAL" && (
                      <Box display={"flex"} sx={{ mb: "16px" }}>
                        <TextInputForm
                          color="secondary"
                          label={formatMessage(messages.businessInviteCode)}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          name="business_profile_activation_code"
                          variant="standard"
                          helperText={formatMessage(
                            messages.didYouReceiveAnInviteCode
                          )}
                          value={values.business_profile_activation_code}
                        />
                      </Box>
                    )}
                    {tenantSettings.collectExternalMembershipNumber && (
                      <Box display={"flex"} sx={{ mb: "16px" }}>
                        <TextInputForm
                          color="secondary"
                          label={formatMessage(messages.aaaMembershipNumber)}
                          name="external_membership_number"
                          variant="standard"
                          helperText={formatMessage(
                            messages.aaaMembershipGetADiscountOnAllTrips
                          )}
                          value={values.external_membership_number}
                          type="text"
                          validate={validateMembershipNumber}
                          required={
                            tenantSettings.collectExternalMembershipNumber &&
                            tenantSettings.requireExternalMembershipNumber
                          }
                        />
                      </Box>
                    )}
                  </Box>
                </React.Fragment>
              )}
              {tenantSettings.paymentsEnabled && (
                <React.Fragment>
                  <Typography className={classes.titleBox} variant="subtitle2">
                    {formatMessage(messages.paymentMethods)}
                  </Typography>
                  <Box
                    className={classes.profileSection}
                    flexDirection="row"
                    header={false}
                  >
                    <PaymentMethods
                      cards={cards}
                      deleteCard={onDeleteCard}
                      markAsDefault={markAsDefault}
                      onAddCard={onAddCard}
                    />
                  </Box>
                </React.Fragment>
              )}
              {!hideConsentOptions && consentDecisions.length > 0 && (
                <React.Fragment>
                  <Typography className={classes.titleBox} variant="subtitle2">
                    {formatMessage(messages.dataPrivacy)}
                  </Typography>
                  <Box
                    className={classes.profileSection}
                    flexDirection="row"
                    header={false}
                  >
                    <Box display={"flex"} flexDirection="column">
                      <FieldArray
                        name="consent_options"
                        render={(arrayHelpers) => (
                          <React.Fragment>
                            {consentDecisions.map((option, index) => (
                              <Field name={"consent_options"}>
                                {({ field, form: { values } }) => (
                                  <div>
                                    <Checkbox
                                      {...field}
                                      name="consent_options"
                                      size="large"
                                      color="secondary"
                                      checked={values.consent_options.includes(
                                        option.id
                                      )}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          arrayHelpers.push(option.id)
                                        } else {
                                          const idx = values.consent_options.indexOf(
                                            option.id
                                          )
                                          arrayHelpers.remove(idx)
                                        }
                                      }}
                                      className={classes.checkbox}
                                    />
                                    {option.description}
                                  </div>
                                )}
                              </Field>
                            ))}
                          </React.Fragment>
                        )}
                      />
                    </Box>
                  </Box>
                </React.Fragment>
              )}
              <Box
                display="flex"
                sx={{
                  marginTop: "24px",
                  marginBottom: "8px",
                  justifyContent: "space-between",
                }}
              >
                <LoadingButton
                  disabled={!isValid || !dirty}
                  text={formatMessage(messages.update)}
                  variant="contained"
                  loading={loading}
                  type="submit"
                  props={{
                    fullWidth: true,
                    startIcon: <RefreshIcon />,
                    size: "large",
                  }}
                />
                <Button
                  disabled={!dirty}
                  color="main"
                  size="small"
                  startIcon={<CloseIcon />}
                  variant="text"
                  className={classes.cancelButton}
                  onClick={() => resetForm()}
                >
                  <FormattedMessage {...messages.cancel} />
                </Button>
              </Box>
            </FormikForm>
          )}
        </FormikFormContainer>
        <Snackbar
          anchorOrigin={{ ...snackBar }}
          open={snackBar.open}
          onClose={() => setSnackbar({ ...snackBar, open: false })}
          message={snackBar.text}
          key={snackBar.vertical + snackBar.horizontal}
          action={<CloseIcon />}
        >
          <MuiAlert
            elevation={6}
            severity={snackBar.severity || "success"}
            variant="filled"
            action={
              <CloseIcon
                onClick={() => setSnackbar({ ...snackBar, open: false })}
              />
            }
          >
            {snackBar.text}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Sidepanel>
  )
}

export default injectIntl(ProfilePanel)
